import { notify } from "@general-backoffice/core";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Spinner } from "reactstrap";

import { type RetryEligibility, subscriptionsEndpoints } from "../../api";
import useFetch from "../../hooks/useFetch";
import getErrorMessage from "../../utils/getErrorMessage";
import { retryButtonState } from "./retryPaymentHint";

interface RetryPaymentButtonProps {
  subscriptionId: number | string;
  /** "admin" shows the decline code and the compact style used in admin tables */
  variant?: "admin" | "user";
  block?: boolean;
  onRetried?: () => void;
}

/**
 * Retries a subscription's failed charges on the current card.
 * The server allows it at most once per day and refuses permanent card errors;
 * this button shows why when it is disabled.
 */
const RetryPaymentButton: React.FC<RetryPaymentButtonProps> = ({ subscriptionId, variant = "user", block, onRetried }) => {
  const { t } = useTranslation("charges", { keyPrefix: "retryPayment" });
  const [isRetrying, setIsRetrying] = useState(false);
  const [override, setOverride] = useState<RetryEligibility | null>(null);

  const { data, isLoading } = useFetch(() => subscriptionsEndpoints.getRetryEligibility(subscriptionId), {
    deps: [subscriptionId],
  });
  const eligibility = override ?? data;
  const state = retryButtonState(eligibility);

  if (isLoading && !eligibility) return <Spinner size="sm" />;
  if (!eligibility || !state.show) return null;

  const retry = async () => {
    setIsRetrying(true);
    try {
      const res = await subscriptionsEndpoints.retryFailedCharges(subscriptionId);
      setOverride(res.eligibility);
      if (res.failed > 0) notify.error(t("resultFailed", { reason: res.lastReason ?? "-" }));
      else if (res.inFlight > 0) notify.info(t("resultProcessing"));
      else notify.success(t("resultPaid", { count: res.paid }));
      onRetried?.();
    } catch (e) {
      notify.error(getErrorMessage(e, t("resultError")));
    } finally {
      setIsRetrying(false);
    }
  };

  const hint =
    !state.enabled && state.hintKey
      ? t(`hint.${state.hintKey}`, {
          time: state.hintKey === "dailyLimit" ? moment(state.nextAllowedAt).format("DD MMM HH:mm") : undefined,
        })
      : null;

  const amount = `${eligibility.amount.toFixed(2)} ${eligibility.currency ?? ""}`.trim();

  return (
    <div className={block ? "w-100" : undefined}>
      <Button
        size="sm"
        color={variant === "admin" ? "primary" : "success"}
        outline={variant === "admin"}
        block={block}
        disabled={!state.enabled || isRetrying}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          retry();
        }}
        title={hint ?? undefined}
      >
        {isRetrying ? <Spinner size="sm" className="mr-2" /> : <i className="fas fa-redo mr-2" aria-hidden="true" />}
        {variant === "admin" ? t("adminButton") : t("userButton", { amount })}
      </Button>
      {hint && (
        <div className="small text-muted mt-1">
          {hint}
          {variant === "admin" && eligibility.reason && <code className="ml-1">{eligibility.reason}</code>}
        </div>
      )}
    </div>
  );
};

export default RetryPaymentButton;
